import React, { useEffect } from 'react';
import { Form, Checkbox, Radio, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import TopAlignCheckbox from 'components/topAlignCheckbox/TopAlignCheckbox';
import './OthersInfoSection.scss';
import TextArea from 'antd/es/input/TextArea';
import {
  fetchRegistrationMediaOptions,
  fetchRegistrationShopsOptions,
} from 'services/registrationService';
import { ShopInfo } from 'types/dto/registration';
import { CommonOption } from 'types/common';

interface OthersInfoSectionProps {
  isReadonly?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onLoadError?: (error: boolean) => void;
}

const OthersInfoSection: React.FC<OthersInfoSectionProps> = ({
  isReadonly = false,
  onLoadingChange,
  onLoadError,
}) => {
  const form = Form.useFormInstance();
  const { t, i18n } = useTranslation('registration');
  const { t: tCommon } = useTranslation('common');

  const selectedCodes: string[] = Form.useWatch('howToKnowUsArray', form) || [];
  const otherText = Form.useWatch('howToKnowUsOthers', form) || '';
  // const finalStr = Form.useWatch('howToKnowUs', form);

  const [hearAboutUsOptions, setHearAboutUsOptions] = React.useState<CommonOption[]>([]);
  const OTHER_CODE = 'O';

  // Helper: sort selected codes according to the original order of options
  const sortByOptionOrder = (codes: string[]): string[] => {
    const optionOrder = hearAboutUsOptions.map(opt => opt.value);
    return [...codes].sort((a, b) => {
      const indexA = optionOrder.indexOf(a);
      const indexB = optionOrder.indexOf(b);
      return indexA - indexB;
    });
  };

  // Build final string with sorted codes
  const buildFinalString = (codes: string[], others: string): string => {
    const sortedCodes = sortByOptionOrder(codes);
    return sortedCodes
      .map(code => {
        if (code === OTHER_CODE) {
          return others ? `${OTHER_CODE}-${others}` : OTHER_CODE;
        }
        return code;
      })
      .join('');
  };

  const handleCheckboxChange = (checkedValue: string, checked: boolean) => {
    let newSelected: string[];
    if (checked) {
      newSelected = [...selectedCodes, checkedValue];
    } else {
      newSelected = selectedCodes.filter(v => v !== checkedValue);
    }
    // Sort the new selection before storing
    const sortedNewSelected = sortByOptionOrder(newSelected);
    form.setFieldValue('howToKnowUsArray', sortedNewSelected);
    const newFinalStr = buildFinalString(sortedNewSelected, otherText);
    form.setFieldValue('howToKnowUs', newFinalStr);
    if (checkedValue === OTHER_CODE && !checked) {
      form.setFieldValue('howToKnowUsOthers', undefined);
    }
  };

  // Update final string when otherText or selectedCodes change (with sorting)
  useEffect(() => {
    const newFinalStr = buildFinalString(selectedCodes, otherText);
    form.setFieldValue('howToKnowUs', newFinalStr);
  }, [otherText, selectedCodes]);

  // Fetch media options
  useEffect(() => {
    const initMedia = async () => {
      try {
        const media = await fetchRegistrationMediaOptions(i18n.language);
        const mappedMedia = media.map((item: any) => ({
          value: item.code,
          label: item.name,
        }));
        setHearAboutUsOptions(mappedMedia);
      } catch (err) {
        console.error('Failed to fetch media options:', err);
      }
    };
    initMedia();
  }, [i18n.language]);

  // Parse existing string into sorted selectedCodes and others
  useEffect(() => {
    const existingStr = form.getFieldValue('howToKnowUs');
    if (existingStr && typeof existingStr === 'string' && selectedCodes.length === 0) {
      const codes: string[] = [];
      let others = '';

      const oIndex = existingStr.indexOf('O-');
      if (oIndex !== -1) {
        const before = existingStr.substring(0, oIndex);
        const afterO = existingStr.substring(oIndex + 2);
        let othersEnd = 0;
        while (othersEnd < afterO.length && !/[A-Z]/.test(afterO[othersEnd])) {
          othersEnd++;
        }
        others = afterO.substring(0, othersEnd);
        const remaining = afterO.substring(othersEnd);

        for (const ch of before) {
          if (/[A-Z]/.test(ch)) codes.push(ch);
        }
        codes.push(OTHER_CODE);
        for (const ch of remaining) {
          if (/[A-Z]/.test(ch)) codes.push(ch);
        }
      } else {
        for (const ch of existingStr) {
          if (/[A-Z]/.test(ch)) codes.push(ch);
        }
      }

      // Sort the parsed codes according to original option order
      const sortedCodes = sortByOptionOrder(codes);
      form.setFieldValue('howToKnowUsArray', sortedCodes);
      if (others) form.setFieldValue('howToKnowUsOthers', others);
    }
  }, [form, selectedCodes.length, hearAboutUsOptions]);

  const [shopOptions, setShopOptions] = React.useState<CommonOption[]>([]);
  useEffect(() => {
    const initShops = async () => {
      try {
        const shops = await fetchRegistrationShopsOptions(i18n.language);
        setShopOptions(shops.map((shop: ShopInfo) => ({
          value: shop.shpRefId,
          label: shop.shpName,
        })));
      } catch (err) {
        console.error('Failed to fetch shop options:', err);
      }
    };
    initShops();
  }, [i18n.language]);

  useEffect(() => {
    if (hearAboutUsOptions.length === 0 && shopOptions.length === 0) {
      onLoadingChange?.(true);
      onLoadError?.(false);
    } else {
      onLoadingChange?.(false);
    }
  }, [hearAboutUsOptions, shopOptions]);

  return (
    <div className="others-info-section-container">
      <div className="vls-section-title">
        {t('communicationPreferencesAndOthers')}
      </div>

      <div className="section-subtitle">{t('communicationPreferences')}</div>

      <Form.Item
        name="commPref"
        valuePropName="checked"
        className="communication-checkbox-item"
      >
        {isReadonly ? (
          <span className="readonly-value">
            {form?.getFieldValue('commPref')
              ? tCommon('label.notReceiveUpdatesText')
              : tCommon('label.receiveUpdatesText')}
          </span>
        ) : (
          <TopAlignCheckbox>
            {tCommon('label.notReceiveUpdatesText')}
          </TopAlignCheckbox>
        )}
      </Form.Item>

      <Divider />

      <div className="section-subtitle">{t('others')}</div>

      <Form.Item name="howToKnowUsArray" hidden noStyle />
      <Form.Item name="howToKnowUs" hidden noStyle />

      <Form.Item label={t('hearAboutUsLabel')}>
        {isReadonly ? (
          <span className="readonly-value">
            {selectedCodes.length > 0
              ? selectedCodes
                  .map((code) => {
                    const option = hearAboutUsOptions.find((opt) => opt.value === code);
                    const label = option ? option.label : code;

                    if (code === OTHER_CODE) {
                      const currentOtherText = form.getFieldValue('howToKnowUsOthers');
                      return currentOtherText ? `${label}: ${currentOtherText}` : label;
                    }
                    return label;
                  })
                  .join(', ')
              : '-'}
          </span>
        ) : (
          <div>
            <div className="hear-about-checkbox-group">
              {hearAboutUsOptions.map(option => {
                const isChecked = selectedCodes.includes(option.value);
                return (
                  <Checkbox
                    key={option.value}
                    checked={isChecked}
                    onChange={e => handleCheckboxChange(option.value, e.target.checked)}
                  >
                    {option.label}
                  </Checkbox>
                );
              })}
            </div>
          {selectedCodes.includes(OTHER_CODE) && (
            <Form.Item
              name="howToKnowUsOthers"
              label={t('others')}
              noStyle={isReadonly}
            >
              {!isReadonly && (
                <TextArea
                  style={{ width: '100%' }}
                  placeholder={t('pleaseSpecify')}
                />
              )}
            </Form.Item>
          )}
          </div>
        )}
      </Form.Item>

      <Form.Item
        name="qn3"
        label={
          isReadonly
            ? t('favoriteShopTypeLabelShort')
            : t('favoriteShopTypeLabel')
        }
        rules={[{ required: true }]}
      >
        {isReadonly ? (
          <span className="readonly-value">
            {form
              ?.getFieldValue('qn3')
              ?.map((value: string) => {
                const option = shopOptions.find(
                  (opt) => opt.value === value
                );
                return option?.label;
              })
              .join(', ') || '-'}
          </span>
        ) : (
          <Checkbox.Group className="shop-type-checkbox-group">
            {shopOptions.map((option) => (
              <Checkbox key={option.value} value={option.value}>
                {option.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        )}
      </Form.Item>

      <Form.Item
        name="language"
        label={t('preferredLanguageLabel')}
        rules={[{ required: true }]}
      >
        {isReadonly ? (
          <span className="readonly-value">
            {form?.getFieldValue('language') === 'en' && t('languageEnglish')}
            {form?.getFieldValue('language') === 'zh' && t('languageChinese')}
            {!form?.getFieldValue('language') && '-'}
          </span>
        ) : (
          <Radio.Group className="language-radio-group">
            <Radio value="en">{t('languageEnglish')}</Radio>
            <Radio value="zh">{t('languageChinese')}</Radio>
          </Radio.Group>
        )}
      </Form.Item>

      <Form.Item
        name="qn2"
        label={t('packingPlasticBagLabel')}
        rules={[{ required: true }]}
      >
        {isReadonly ? (
          <span className="readonly-value">
            {form?.getFieldValue('qn2') === 'yes' && t('packingYes')}
            {form?.getFieldValue('qn2') === 'no' && t('packingNo')}
            {!form?.getFieldValue('qn2') && '-'}
          </span>
        ) : (
          <Radio.Group className="packing-radio-group">
            <Radio value="yes">{t('packingYes')}</Radio>
            <Radio value="no">{t('packingNo')}</Radio>
          </Radio.Group>
        )}
      </Form.Item>
    </div>
  );
};

export default OthersInfoSection;